// src/features/tickets/hooks/useTicketReplyTargets.ts
//
// Rein lesender Hook: liefert die CC-Liste fuer "Allen antworten" eines Tickets.
// Die Aufloesung (juengste eingehende Nachricht -> urspruengliche To/CC-Empfaenger,
// abzueglich eigener @xtax.de-Adressen und des Verfassers) liegt serverseitig in der
// RPC get_ticket_reply_targets; das Frontend dupliziert diese Logik nicht.
//
// Der Antwort-Empfaenger ("An") wird dagegen synchron aus den bereits geladenen
// Ticket-Nachrichten bestimmt (siehe TicketDetail) und kommt nicht aus diesem Hook.

import { useState, useEffect } from 'react';
import { supabase } from '../../../shared/services/supabase';

export interface UseTicketReplyTargetsResult {
  loading: boolean;
  replyAllCc: string[];
}

interface ReplyTargetsRow {
  reply_to: string | null;
  reply_all_cc: string[] | null;
}

export const useTicketReplyTargets = (
  ticketId: string | null | undefined
): UseTicketReplyTargetsResult => {
  const [loading, setLoading] = useState(false);
  const [replyAllCc, setReplyAllCc] = useState<string[]>([]);

  useEffect(() => {
    if (!ticketId) {
      setReplyAllCc([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_ticket_reply_targets', {
          p_ticket_id: ticketId
        });

        if (cancelled) return;

        if (error) {
          console.error('[useTicketReplyTargets] RPC error:', error);
          setReplyAllCc([]);
          return;
        }

        const rows = (data ?? []) as ReplyTargetsRow[];
        const cc = rows.length > 0 && Array.isArray(rows[0].reply_all_cc)
          ? rows[0].reply_all_cc
          : [];
        setReplyAllCc(cc);
      } catch (err) {
        console.error('[useTicketReplyTargets] Unexpected error:', err);
        if (!cancelled) setReplyAllCc([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  return { loading, replyAllCc };
};
